import { useState, useEffect, useMemo } from "react";
import { z } from "zod";
import { createRideApi } from "services/auth/rideApis";
import MapAutoComplete from "@/components/MapAutoComplete.jsx";
import QuickHubs from "@/components/QuickHubs";
import { useNavigate } from "react-router-dom";
import { Label } from "../components/ui/label.js";
import { Input } from "../components/ui/input.js";
import { Button } from "../components/ui/button.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select.js";
import toast from "react-hot-toast";
import {
  getInterestedUsersApi,
  getTopRequestedRoutesApi,
} from "services/auth/interestApis.js";
import { getUserProfileApi } from "services/auth/authApis.js";
import { FiMapPin, FiUsers, FiCalendar, FiCreditCard, FiNavigation, FiFileText, FiShield } from "react-icons/fi";
import TopRequestedRoutes from "@/components/TopRequestedRoutes";
import type { RouteSuggestion } from "@/components/TopRequestedRoutes";
const locationSchema = z
  .string()
  .min(3, "Location must be at least 3 characters")
  .max(200, "Location is too long")
  .regex(
    /^[a-zA-Z0-9\s,.'\-()]+$/,
    "Location contains invalid special characters (no symbols like $, ?, !)"
  )
  .regex(
    /[a-zA-Z]{2,}/,
    "Location must contain valid alphanumeric characters"
  )
  .refine((val) => !/(.)\1{2,}/i.test(val), {
    message: "Location cannot contain repetitive characters (e.g. 'kkk', 'bbb')",
  })
  .refine((val) => !/\b[bcdfghjklmnpqrstvwxyz]{5,}\b/i.test(val), {
    message: "Location appears to be gibberish (contains unnatural consonant sequences)",
  })
  .refine((val) => /[aeiouy0-9]/i.test(val), {
    message: "Location must be a valid place name",
  });

const createRideSchema = z
  .object({
    from: locationSchema,
    to: locationSchema,
    capacity: z
      .number()
      .int()
      .min(1, "Capacity must be at least 1")
      .max(20, "Capacity must not exceed 20"),
    date: z.string().refine((val) => new Date(val) > new Date(), {
      message: "Ride datetime must be 30 min in the future",
    }),
    totalFare: z
      .number()
      .int()
      .min(0, "Per head cost must be at least 0")
      .max(5000, "Per head cost must not exceed 5000"),
    vehicleType: z.enum(["CAR", "BIKE", "VAN", "AUTO"]),
    description: z.string().optional(),
    recurrence: z
      .object({
        isRecurring: z.boolean(),
        recurrenceEndDate: z.string().optional(),
        selectedDays: z.array(z.number()).optional(),
      })
      .optional(),
  })
  .refine((data) => data.from.trim().toLowerCase() !== data.to.trim().toLowerCase(), {
    message: "Source and destination cannot be the same",
    path: ["to"],
  })
  .refine(
    (data) => {
      if (data.recurrence?.isRecurring) {
        return (
          data.recurrence.recurrenceEndDate &&
          data.recurrence.selectedDays &&
          data.recurrence.selectedDays.length > 0 &&
          new Date(data.recurrence.recurrenceEndDate) > new Date(data.date)
        );
      }
      return true;
    },
    {
      message:
        "End date must be after start date and at least one day must be selected",
      path: ["recurrence"],
    }
  );

type CreateRideForm = z.infer<typeof createRideSchema>;



export const CreateRide = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rideForm, setRideForm] = useState<
    Omit<CreateRideForm, "capacity" | "totalFare"> & {
      capacity: string;
      totalFare: string;
    }
  >({
    from: "",
    to: "",
    capacity: "",
    date: "",
    totalFare: "",
    vehicleType: "CAR",
    description: "",
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof CreateRideForm, string>>
  >({});
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState("");
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [topSuggestions, setTopSuggestions] = useState<RouteSuggestion[]>([]);
  const [checkInterestedUsers, setCheckInterestedUsers] = useState<string>("");
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [genderPref, setGenderPref] = useState<"SAME" | "ANY">("SAME");

  useEffect(() => {
    async function fetchUser() {
      try {
        const profile = await getUserProfileApi();
        if (profile) {
          setCurrentUser(profile);
          setGenderPref(profile.genderPreference || "SAME");
        }
      } catch (err) {
        console.error("fetchUser error:", err);
      }
    }
    fetchUser();
  }, []);

  useEffect(() => {
    async function fetchTopInterests() {
      try {
        const response = await getTopRequestedRoutesApi();
        setTopSuggestions(response || []);
      } catch (error) {
        console.error("Error fetching top interests:", error);
      }
    }

    fetchTopInterests();
  }, []);

  useEffect(() => {
    setCheckInterestedUsers("");
    async function fetchInterestedUsers() {
      const response = await getInterestedUsersApi(rideForm.from, rideForm.to);
      if (response > 1)
        setCheckInterestedUsers(
          `${response} users are interested in this route.`,
        );
    }

    if (rideForm.from.length > 0 && rideForm.to.length > 0) {
      fetchInterestedUsers();
    }
  }, [rideForm.from, rideForm.to]);

  const [fromVerified, setFromVerified] = useState(false);
  const [toVerified, setToVerified] = useState(false);

  // Handler for GoogleMapsAutocomplete
  const setFrom = (val: string, verified = false) => {
    setRideForm((prev) => ({ ...prev, from: val }));
    setFromVerified(verified);
    if (val.trim() && rideForm.to.trim() && val.trim().toLowerCase() === rideForm.to.trim().toLowerCase()) {
      setErrors((prev) => ({ ...prev, to: "Source and destination cannot be the same" }));
    } else {
      setErrors((prev) => ({
        ...prev,
        from: undefined,
        ...(prev.to === "Source and destination cannot be the same" ? { to: undefined } : {}),
      }));
    }
  };

  const setTo = (val: string, verified = false) => {
    setRideForm((prev) => ({ ...prev, to: val }));
    setToVerified(verified);
    if (val.trim() && rideForm.from.trim() && val.trim().toLowerCase() === rideForm.from.trim().toLowerCase()) {
      setErrors((prev) => ({ ...prev, to: "Source and destination cannot be the same" }));
      toast.error("Source and destination cannot be the same");
    } else {
      setErrors((prev) => ({ ...prev, to: undefined }));
    }
  };

  const applyQuickHub = (field: "from" | "to", hub: { label: string; address: string }) => {
    if (field === "from") {
      setFrom(hub.address, true);
    } else {
      setTo(hub.address, true);
    }
  };

  const daysOfWeek = [
    { label: "Sun", value: 0 },
    { label: "Mon", value: 1 },
    { label: "Tue", value: 2 },
    { label: "Wed", value: 3 },
    { label: "Thu", value: 4 },
    { label: "Fri", value: 5 },
    { label: "Sat", value: 6 },
  ];

  const toggleDay = (day: number) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  // Preview count for recurring rides
  const previewCount = useMemo(() => {
    if (!isRecurring || !rideForm.date || !recurrenceEndDate || selectedDays.length === 0) return 0;
    const start = new Date(rideForm.date);
    const end = new Date(recurrenceEndDate);
    if (end <= start) return 0;
    let count = 0;
    const current = new Date(start);
    while (current <= end) {
      if (selectedDays.includes(current.getDay())) count++;
      current.setDate(current.getDate() + 1);
    }
    return count;
  }, [isRecurring, rideForm.date, recurrenceEndDate, selectedDays]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (
      rideForm.from.trim().toLowerCase() === rideForm.to.trim().toLowerCase() &&
      rideForm.from.trim() !== ""
    ) {
      setErrors({ to: "Source and destination cannot be the same" });
      toast.error("Source and destination cannot be the same");
      return;
    }

    if (!fromVerified) {
      setErrors((prev) => ({
        ...prev,
        from: "Please select a recognized location from the suggestions or quick hubs",
      }));
      toast.error("Please pick a recognized pickup location from suggestions or quick hubs");
      return;
    }

    if (!toVerified) {
      setErrors((prev) => ({
        ...prev,
        to: "Please select a recognized location from the suggestions or quick hubs",
      }));
      toast.error("Please pick a recognized destination from suggestions or quick hubs");
      return;
    }

    try {
      const recurrenceData = isRecurring
        ? { isRecurring: true, recurrenceEndDate, selectedDays }
        : undefined;

      // Convert string values to numbers for validation
      const formData = {
        ...rideForm,
        capacity: rideForm.capacity ? parseInt(rideForm.capacity, 10) : 0,
        totalFare: rideForm.totalFare ? parseInt(rideForm.totalFare, 10) : 0,
        recurrence: recurrenceData,
      };

      // Validate form data
      createRideSchema.parse(formData);

      setLoading(true);
      const data = await createRideApi(
        formData.from,
        formData.to,
        formData.capacity,
        formData.date,
        formData.totalFare,
        formData.vehicleType,
        formData.description || "",
        recurrenceData,
        currentUser?.gender === "FEMALE" ? genderPref : undefined
      );

      if (data) {
        navigate("/");
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        const fieldErrors: Partial<Record<keyof CreateRideForm, string>> = {};
        err.issues.forEach((issue) => {
          const field = issue.path[0] as keyof CreateRideForm;
          fieldErrors[field] = issue.message;
        });
        setErrors(fieldErrors);

        const firstIssue = err.issues[0];
        if (firstIssue) {
          toast.error(firstIssue.message);
        }
      } else {
        const errorMessage =
          (err as any)?.response?.data?.message ||
          (err as any)?.message ||
          "An error occurred. Please try again.";
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 30); // Add 0.5 hour (30 min) padding
    const pad = (n: number) => n.toString().padStart(2, "0");
    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1);
    const day = pad(now.getDate());
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleChooseRouteSuggsestion = (route: {
    from: string;
    to: string;
  }) => {
    setRideForm({ ...rideForm, from: route.from, to: route.to });
    setFromVerified(true);
    setToVerified(true);
    setErrors((prev) => ({ ...prev, from: undefined, to: undefined }));
  };

  function sliceAddress(address: string, maxLength = 40) {
    if (!address || address.length <= maxLength) return address;

    const sliced = address.slice(0, maxLength);
    const lastSpace = sliced.lastIndexOf(" ");

    return sliced.slice(0, lastSpace) + "...";
  }

  return (
    <div className="flex flex-col items-center min-h-screen w-full z-10 py-8 px-3 sm:px-4">
      <div className="w-full max-w-xl mx-auto h-fit">
        {/* Page title — floating above the card */}
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight drop-shadow">
            Publish a Ride
          </h1>
          <p className="text-white/70 text-xs sm:text-sm mt-1">
            Share your commute or trip with fellow MUJ campus mates
          </p>
        </div>

        {/* Create Ride card — matching SearchRide's sleek, floating aesthetic */}
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl shadow-xl border border-zinc-100 w-full overflow-hidden"
          style={{ minHeight: "fit-content" }}
        >
          {/* Top Requested Routes Shelf (if available) */}
          {topSuggestions.length > 0 && (
            <div className="p-3.5 sm:p-4 bg-amber-50/50 border-b border-zinc-100">
              <TopRequestedRoutes
                routes={topSuggestions}
                currentFrom={rideForm.from}
                currentTo={rideForm.to}
                onSelectRoute={handleChooseRouteSuggsestion}
              />
            </div>
          )}

          {/* Section 1: Route — Side by side From / To matching SearchRide */}
          <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-zinc-100 border-b border-zinc-100">
            {/* From */}
            <div className="p-4 sm:p-5 space-y-1.5">
              <Label htmlFor="from" className="flex items-center gap-1.5 text-zinc-500 text-xs font-semibold uppercase tracking-wider">
                <FiMapPin className="w-3.5 h-3.5 text-emerald-500" />
                From (Pickup) <span className="text-amber-500">*</span>
              </Label>
              <MapAutoComplete
                value={rideForm.from}
                placeholder="Enter pickup location (e.g. MUJ Campus)"
                changeHandler={setFrom}
                requireSelection={true}
                isVerified={fromVerified}
                onVerifiedChange={setFromVerified}
              />
              {errors.from && (
                <p className="text-red-500 text-[11px] font-medium mt-1">{errors.from}</p>
              )}
            </div>

            {/* To */}
            <div className="p-4 sm:p-5 space-y-1.5">
              <Label htmlFor="to" className="flex items-center gap-1.5 text-zinc-500 text-xs font-semibold uppercase tracking-wider">
                <FiMapPin className="w-3.5 h-3.5 text-amber-500" />
                To (Destination) <span className="text-amber-500">*</span>
              </Label>
              <MapAutoComplete
                value={rideForm.to}
                placeholder="Enter destination (e.g. Jaipur Junction)"
                changeHandler={setTo}
                requireSelection={true}
                isVerified={toVerified}
                onVerifiedChange={setToVerified}
              />
              {errors.to && (
                <p className="text-red-500 text-[11px] font-medium mt-1">{errors.to}</p>
              )}
            </div>
          </div>

          {/* Quick Hubs Shelf — 2-column compact pills matching SearchRide */}
          <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-zinc-100 border-b border-zinc-100 bg-zinc-50/60">
            <div className="p-3 sm:px-4 sm:py-3">
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-2">Pickup hubs</p>
              <QuickHubs
                compact
                currentValue={rideForm.from}
                disabledValue={rideForm.to}
                onSelect={(hub: any) => applyQuickHub("from", hub)}
              />
            </div>
            <div className="p-3 sm:px-4 sm:py-3">
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-2">Drop-off hubs</p>
              <QuickHubs
                compact
                currentValue={rideForm.to}
                disabledValue={rideForm.from}
                onSelect={(hub: any) => applyQuickHub("to", hub)}
              />
            </div>
          </div>

          {/* Interested Users notification badge if active */}
          {checkInterestedUsers.length > 0 && (
            <div className="px-4 py-2.5 bg-emerald-50/70 border-b border-zinc-100 flex items-center gap-2 text-emerald-800 text-xs font-medium">
              <FiUsers className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span>{checkInterestedUsers}</span>
            </div>
          )}

          {/* Section 2: Departure, Capacity, Vehicle & Cost */}
          <div className="p-4 sm:p-5 space-y-4 border-b border-zinc-100">
            <div className="flex items-center justify-between pb-1">
              <span className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider">
                Ride Details
              </span>
              <span className="text-[11px] text-zinc-400 font-medium">
                * All marked fields are mandatory
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 sm:gap-4">
              {/* Date & Time */}
              <div className="space-y-1.5">
                <Label htmlFor="date" className="flex items-center gap-1.5 text-zinc-600 text-xs font-semibold uppercase tracking-wider">
                  <FiCalendar className="w-3.5 h-3.5 text-zinc-400" />
                  Departure Date & Time <span className="text-amber-500">*</span>
                </Label>
                <Input
                  type="datetime-local"
                  id="date"
                  name="date"
                  value={rideForm.date}
                  onChange={(e) => {
                    setRideForm({ ...rideForm, date: e.target.value });
                    setErrors({ ...errors, date: undefined });
                  }}
                  min={getMinDateTime()}
                  className={`h-11 rounded-xl bg-zinc-50/50 hover:bg-white focus:bg-white border-zinc-200 text-sm font-medium transition-all ${
                    errors.date ? "border-red-500 ring-1 ring-red-500" : ""
                  }`}
                />
                {errors.date && (
                  <p className="text-red-500 text-[11px] font-medium">{errors.date}</p>
                )}
              </div>

              {/* Seats Available */}
              <div className="space-y-1.5">
                <Label htmlFor="capacity" className="flex items-center gap-1.5 text-zinc-600 text-xs font-semibold uppercase tracking-wider">
                  <FiUsers className="w-3.5 h-3.5 text-zinc-400" />
                  Seats Available <span className="text-amber-500">*</span>
                </Label>
                <Input
                  type="number"
                  id="capacity"
                  name="capacity"
                  value={rideForm.capacity}
                  onChange={(e) => {
                    setRideForm({ ...rideForm, capacity: e.target.value });
                    setErrors({ ...errors, capacity: undefined });
                  }}
                  min="1"
                  max="20"
                  placeholder="e.g. 3"
                  className={`h-11 rounded-xl bg-zinc-50/50 hover:bg-white focus:bg-white border-zinc-200 text-sm font-medium transition-all ${
                    errors.capacity ? "border-red-500 ring-1 ring-red-500" : ""
                  }`}
                />
                {errors.capacity && (
                  <p className="text-red-500 text-[11px] font-medium">{errors.capacity}</p>
                )}
              </div>

              {/* Vehicle Type */}
              <div className="space-y-1.5">
                <Label htmlFor="vehicleType" className="flex items-center gap-1.5 text-zinc-600 text-xs font-semibold uppercase tracking-wider">
                  <FiNavigation className="w-3.5 h-3.5 text-zinc-400" />
                  Vehicle Type <span className="text-amber-500">*</span>
                </Label>
                <Select
                  value={rideForm.vehicleType}
                  onValueChange={(value) => {
                    setRideForm({
                      ...rideForm,
                      vehicleType: value as "CAR" | "BIKE" | "VAN" | "AUTO",
                    });
                    setErrors({ ...errors, vehicleType: undefined });
                  }}
                >
                  <SelectTrigger
                    className={`h-11 rounded-xl bg-zinc-50/50 hover:bg-white focus:bg-white border-zinc-200 text-sm font-medium transition-all ${
                      errors.vehicleType ? "border-red-500 ring-1 ring-red-500" : ""
                    }`}
                  >
                    <SelectValue placeholder="Select vehicle type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CAR">🚗 Car</SelectItem>
                    <SelectItem value="BIKE">🏍️ Bike</SelectItem>
                    <SelectItem value="VAN">🚐 Van</SelectItem>
                    <SelectItem value="AUTO">🛺 Auto</SelectItem>
                  </SelectContent>
                </Select>
                {errors.vehicleType && (
                  <p className="text-red-500 text-[11px] font-medium">{errors.vehicleType}</p>
                )}
              </div>

              {/* Cost per Seat */}
              <div className="space-y-1.5">
                <Label htmlFor="totalFare" className="flex items-center gap-1.5 text-zinc-600 text-xs font-semibold uppercase tracking-wider">
                  <FiCreditCard className="w-3.5 h-3.5 text-zinc-400" />
                  Cost per Seat (₹) <span className="text-amber-500">*</span>
                </Label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400 font-semibold text-sm pointer-events-none">
                    ₹
                  </span>
                  <Input
                    type="number"
                    id="totalFare"
                    name="totalFare"
                    value={rideForm.totalFare}
                    onChange={(e) => {
                      setRideForm({ ...rideForm, totalFare: e.target.value });
                      setErrors({ ...errors, totalFare: undefined });
                    }}
                    min="0"
                    max="5000"
                    placeholder="0 for free ride"
                    className={`h-11 pl-8 rounded-xl bg-zinc-50/50 hover:bg-white focus:bg-white border-zinc-200 text-sm font-medium transition-all ${
                      errors.totalFare ? "border-red-500 ring-1 ring-red-500" : ""
                    }`}
                  />
                </div>
                {errors.totalFare && (
                  <p className="text-red-500 text-[11px] font-medium">{errors.totalFare}</p>
                )}
              </div>
            </div>

            {/* Recurring Ride Sub-Section */}
            <div className="border border-zinc-200/80 rounded-xl p-3.5 space-y-3 bg-zinc-50/40">
              <div className="flex items-center justify-between">
                <Label htmlFor="recurring" className="cursor-pointer font-medium text-xs sm:text-sm text-zinc-700 flex items-center gap-2">
                  <FiCalendar className="w-4 h-4 text-amber-500" /> Make this a recurring ride
                </Label>
                <button
                  type="button"
                  id="recurring"
                  role="switch"
                  aria-checked={isRecurring}
                  onClick={() => setIsRecurring(!isRecurring)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                    isRecurring ? "bg-amber-500" : "bg-zinc-200"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-xs transition-transform ${
                      isRecurring ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              {isRecurring && (
                <div className="space-y-3 pt-3 border-t border-zinc-200/60 animate-in fade-in-50">
                  <div>
                    <Label htmlFor="recurrenceEndDate" className="flex items-center gap-1.5 mb-1.5 text-xs font-semibold text-zinc-600">
                      End Date <span className="text-amber-500">*</span>
                    </Label>
                    <Input
                      type="date"
                      id="recurrenceEndDate"
                      name="recurrenceEndDate"
                      value={recurrenceEndDate}
                      onChange={(e) => setRecurrenceEndDate(e.target.value)}
                      min={rideForm.date ? rideForm.date.split("T")[0] : ""}
                      className={`h-10 rounded-xl bg-white text-sm ${errors.recurrence ? "border-red-500" : ""}`}
                    />
                  </div>

                  <div>
                    <Label className="flex items-center gap-1.5 mb-1.5 text-xs font-semibold text-zinc-600">
                      Repeat on days <span className="text-amber-500">*</span>
                    </Label>
                    <div className="flex flex-wrap gap-1.5">
                      {daysOfWeek.map((day) => (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => toggleDay(day.value)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all cursor-pointer ${
                            selectedDays.includes(day.value)
                              ? "bg-amber-500 text-white border-amber-500 shadow-xs"
                              : "bg-white text-zinc-700 border-zinc-200 hover:border-amber-300"
                          }`}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {previewCount > 0 && (
                    <p className="text-xs text-amber-800 font-medium bg-amber-50 border border-amber-200/80 px-3 py-2 rounded-lg">
                      ✨ Will automatically create <strong>{previewCount} rides</strong> between{" "}
                      {rideForm.date ? rideForm.date.split("T")[0] : "start"} and{" "}
                      {recurrenceEndDate || "end"}
                    </p>
                  )}

                  {(errors as any).recurrence && (
                    <p className="text-red-500 text-xs font-medium">
                      {(errors as any).recurrence}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Section 3: Preferences, Notes & Submit Action */}
          <div className="p-4 sm:p-5 space-y-4">
            {currentUser?.gender === "FEMALE" && (
              <div className="space-y-2 p-3.5 rounded-xl bg-pink-50/70 border border-pink-200/80">
                <div className="flex items-center gap-2">
                  <FiShield className="w-4 h-4 text-pink-600" />
                  <Label className="text-xs font-bold text-pink-900 uppercase tracking-wider">
                    Ride Passenger Restriction
                  </Label>
                </div>
                <Select
                  value={genderPref}
                  onValueChange={(val: "SAME" | "ANY") => setGenderPref(val)}
                >
                  <SelectTrigger className="bg-white rounded-xl border-pink-200 text-sm font-medium h-10">
                    <SelectValue placeholder="Select passenger restriction" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SAME">
                      ♀ Female Only (Only female passengers can join)
                    </SelectItem>
                    <SelectItem value="ANY">
                      ⚥ Open to Any Gender (Unbiased, anyone can join)
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-pink-700">
                  {genderPref === "SAME"
                    ? "Only female passengers can search and request to join this ride."
                    : "Unbiased: Any gender passenger can join. You can update this anytime."}
                </p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="description" className="flex items-center gap-1.5 text-zinc-600 text-xs font-semibold uppercase tracking-wider">
                <FiFileText className="w-3.5 h-3.5 text-zinc-400" />
                Description / Notes (Optional)
              </Label>
              <textarea
                id="description"
                name="description"
                value={rideForm.description}
                onChange={(e) => {
                  setRideForm({ ...rideForm, description: e.target.value });
                  setErrors({ ...errors, description: undefined });
                }}
                placeholder="e.g. Leaving from gate 2, boot space available for luggage..."
                className={`w-full h-20 resize-none border rounded-xl px-3.5 py-2.5 text-sm bg-zinc-50/50 hover:bg-white focus:bg-white focus:outline-hidden focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all ${
                  errors.description ? "border-red-500 ring-1 ring-red-500" : "border-zinc-200"
                }`}
              />
              {errors.description && (
                <p className="text-red-500 text-[11px] font-medium">{errors.description}</p>
              )}
            </div>

            {/* Submit Action matching SearchRide's dark button */}
            <Button
              type="submit"
              disabled={loading}
              className={`w-full h-12 text-sm sm:text-base font-semibold bg-zinc-900 text-white hover:bg-zinc-800 rounded-xl transition-all shadow-md active:scale-[0.99] cursor-pointer mt-2 ${
                loading ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              {loading ? "Publishing Ride..." : "Publish Ride"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRide;
